import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDialog } from '../context/DialogContext';
import { useUser } from '../context/UserContext';
import Modal from '../components/Modal';
import EventCreatePage from './EventCreatePage';
import gatheringService from '../services/gatheringService';
import useIsMobile from '../hooks/useIsMobile';
import useContainerWidth from '../hooks/useContainerWidth';
import { computeOverlaps, overlaps } from '../utils/agendaOverlap';
import { SurfacePage, SoftSurface, StickyRail, RING_ON } from '../components/surface';
import { PillButton, TagPill } from '../components/club/clubUi';
import AddPeopleModal from '../components/AddPeopleModal';
import { Sheet, Segmented, OverlapChip, BigSwitch, Field, KVRows, FacePill, pillInput, softTextarea, kicker, CLASH_BG, CLASH_FG, CLASH_BAR, STARTER_TRACK, RED_TINT } from '../components/gathering/gatheringUi';
import { dayKey, rangeDays, priceLabel, groupByDay } from '../components/gathering/gatheringModel';
import { posterPlaceholder } from '../utils/posterPlaceholder';
import { money, plural, timeRangeLabel, dayLabel, whenLabel } from '../utils/surfaceFormat';
import ProCoverEditor from '../components/pro/ProCoverEditor';
import GatheringCover from '../components/gathering/GatheringCover';
import useGatheringCover from '../components/gathering/useGatheringCover';
import { GATHERING_COVER_COPY } from '../components/gathering/gatheringCoverModel';

/**
 * /clubs/:id/gatherings/new · /gatherings/new · …/:gatheringId/edit — design_handoff_gatherings
 * §3 "Builder". Three clickable step pills (DETAILS · AGENDA · REVIEW), white panels with pill
 * inputs and ring-active segmented cards, day lanes for the agenda with the clash banner, a
 * READ IT BACK review, one footer, and on wide screens a live HOW IT READS IN THE CLUB card.
 * The data flow is unchanged: step 1 creates (or updates) the gathering, step 2 attaches
 * events through the embedded builder or the link sheet, step 3 publishes or keeps the draft.
 */

const MUTED = '#6B7280';
const SUBTLE = '#9CA3AF';
const BODY = '#374151';
const RULE = '2px solid #F2F2F2';
const STEPS = [[1, 'DETAILS'], [2, 'AGENDA'], [3, 'COVER & REVIEW']];

// ISO → local YYYY-MM-DD for a <input type="date">.
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const shortDate = (ymd) => (ymd ? dayLabel(`${ymd}T00:00:00`) : '—');
// A date-input value is a LOCAL day: send local midnight, so it reads back (toDateInput) as the
// same day everywhere. `new Date('2026-11-20')` is UTC midnight, which came back as Nov 19 in BC.
const localMidnightIso = (ymd) => new Date(`${ymd}T00:00:00`).toISOString();

/**
 * A draft that survives a reload. The wizard saves to the server on every step change, so this
 * only ever holds what was typed since then — but losing that to an accidental refresh is the
 * complaint, so it is kept per gathering (or per "new") and dropped the moment it is saved or
 * deliberately discarded. Storage failures (private windows, blocked site data) are not errors:
 * the wizard simply behaves as it did before.
 */
const draftKey = (clubId, editId) => `pr.gatheringDraft.${editId || `new:${clubId || 'standalone'}`}`;
const readDraft = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // A stale draft is worse than none: a week later nobody remembers typing it.
    if (!parsed || typeof parsed !== 'object' || Date.now() - (parsed.at || 0) > 7 * 86400000) return null;
    return parsed.details && typeof parsed.details === 'object' ? parsed : null;
  } catch { return null; }
};
const writeDraft = (key, details) => {
  try { window.localStorage.setItem(key, JSON.stringify({ at: Date.now(), details })); } catch { /* no storage */ }
};
const clearDraft = (key) => {
  try { window.localStorage.removeItem(key); } catch { /* no storage */ }
};

export default function GatheringWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: clubId, gatheringId: editId } = useParams();
  const isEdit = !!editId;
  const { showConfirm, showAlert } = useDialog();
  const { profile } = useUser();
  const mobile = useIsMobile();
  const mainRef = useRef(null);
  const width = useContainerWidth(mainRef);
  const wide = !mobile && width >= 1040;

  const [step, setStep] = useState(1);
  const [gatheringId, setGatheringId] = useState(editId || null);
  const [details, setDetails] = useState({ name: '', description: '', startDate: '', endDate: '', isPrivate: true, mode: 'open', chargeFee: false, entryFee: '', fundingType: 'guaranteed', fundingGoal: '', fundingDeadline: '' });
  const [agenda, setAgenda] = useState([]);
  const [loaded, setLoaded] = useState(null); // the saved gathering (edit mode / after step 1)
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const savedDetails = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [attachable, setAttachable] = useState([]);
  const [touched, setTouched] = useState(false);
  // Co-hosts picked before the gathering exists ride the create call; once it exists they save
  // straight through (POST/DELETE …/cohosts) and the list is read back off the gathering.
  const [pendingCoHosts, setPendingCoHosts] = useState([]);
  const [coHostPickerOpen, setCoHostPickerOpen] = useState(false);
  const draftId = draftKey(clubId, editId);
  // A draft recovered from a previous visit. Shown as a banner rather than applied silently —
  // quietly overwriting what the server has is how a host loses the edit they did keep.
  const [recovered, setRecovered] = useState(() => readDraft(draftId));
  // The cover document rides the saved gathering; the editor saves straight through the API
  // (photos are not part of the details form, so they never read as unsaved).
  const { shapedCover, setCover, coverPhotos, layoutKey, onFocal, api: coverApi } = useGatheringCover(loaded);
  const previewRef = useRef(null);
  const previewWidth = useContainerWidth(previewRef);

  if (savedDetails.current === null) savedDetails.current = JSON.stringify(details);
  const hasUnsavedDetails = JSON.stringify(details) !== savedDetails.current;
  // Keep the unsaved typing where a refresh can't take it. Debounced: this runs per keystroke.
  useEffect(() => {
    if (!hasUnsavedDetails) return undefined;
    const t = window.setTimeout(() => writeDraft(draftId, details), 400);
    return () => window.clearTimeout(t);
  }, [hasUnsavedDetails, details, draftId]);
  useEffect(() => {
    if (!hasUnsavedDetails) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedDetails]);

  const setD = (patch) => setDetails((d) => ({ ...d, ...patch }));

  /** Re-read the saved gathering (after cover uploads the rows come back with their ids). */
  const reloadGathering = async (gid = gatheringId) => {
    if (!gid) return;
    const r = await gatheringService.get(gid);
    if (r.success && r.data) setLoaded(r.data);
  };

  const refreshAgenda = async (gid = gatheringId) => {
    const r = await gatheringService.get(gid);
    const list = r.success ? (r.data.agenda || []) : [];
    if (r.success) setLoaded(r.data);
    setAgenda(list);
    return list;
  };

  // Edit mode: load the existing gathering + agenda and jump to the agenda step
  // so the host can add/remove events after creation.
  useEffect(() => {
    if (!editId) return;
    (async () => {
      const r = await gatheringService.get(editId);
      if (r.success && r.data) {
        const g = r.data;
        setLoaded(g);
        const initialDetails = {
          name: g.name || '',
          description: g.description || '',
          startDate: toDateInput(g.startsAt),
          endDate: toDateInput(g.endsAt),
          isPrivate: !!g.isPrivate,
          mode: g.mode || 'open',
          chargeFee: (g.entryFeeCents || 0) > 0,
          entryFee: g.entryFeeCents ? (g.entryFeeCents / 100).toString() : '',
          fundingType: g.fundingType || 'guaranteed',
          fundingGoal: g.fundingGoalCents ? (g.fundingGoalCents / 100).toString() : '',
          fundingDeadline: g.fundingDeadline ? toDateInput(g.fundingDeadline) : '',
        };
        savedDetails.current = JSON.stringify(initialDetails);
        setDetails(initialDetails);
        setAgenda(g.agenda || []);
        setStep(2);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ── Co-hosts ──────────────────────────────────────────────────────────────
  const canPickCoHosts = !gatheringId || !!loaded?.isOwner;
  const coHostList = gatheringId ? (loaded?.hosts || []).filter((h) => h.role === 'cohost') : pendingCoHosts;
  const addWizardCoHosts = async (userIds, picked) => {
    setCoHostPickerOpen(false);
    if (!gatheringId) {
      setPendingCoHosts((cur) => [...cur, ...(picked || []).filter((p) => !cur.some((c) => c.userId === p.userId))]);
      return;
    }
    const results = await Promise.all((userIds || []).map((id) => gatheringService.addCoHost(gatheringId, id)));
    const failed = results.find((x) => !x.success);
    if (failed) await showAlert({ title: 'COULD NOT ADD CO-HOST', message: failed.error?.message || 'Something went wrong.', type: 'error' });
    await reloadGathering();
  };
  const removeWizardCoHost = async (p) => {
    if (!gatheringId) { setPendingCoHosts((cur) => cur.filter((c) => c.userId !== p.userId)); return; }
    const r = await gatheringService.removeCoHost(gatheringId, p.userId);
    if (!r.success) { await showAlert({ title: 'COULD NOT REMOVE', message: r.error?.message || 'Something went wrong.', type: 'error' }); return; }
    await reloadGathering();
  };

  // ── Step 1: details → create/update the gathering ──────────────────────────
  const dateErr = !!(details.startDate && details.endDate && details.endDate < details.startDate);
  const detailsValid = !!details.name.trim() && !!details.startDate && !!details.endDate && !dateErr;
  const submitDetails = async () => {
    setTouched(true);
    if (!detailsValid) return false;
    setBusy(true);
    const payload = {
      name: details.name.trim(),
      description: details.description.trim() || null,
      startsAt: localMidnightIso(details.startDate),
      endsAt: localMidnightIso(details.endDate),
      isPrivate: details.isPrivate,
      mode: details.mode,
      fundingType: details.fundingType,
      entryFeeCents: details.chargeFee ? Math.round(parseFloat(details.entryFee || '0') * 100) : 0,
      fundingGoalCents: details.chargeFee && details.fundingType === 'party_starter' && details.fundingGoal
        ? Math.round(parseFloat(details.fundingGoal) * 100) : null,
      fundingDeadline: details.chargeFee && details.fundingType === 'party_starter' && details.fundingDeadline
        ? localMidnightIso(details.fundingDeadline) : null,
      ...(gatheringId ? {} : { coHostUserIds: pendingCoHosts.map((p) => p.userId) }),
    };
    const res = gatheringId
      ? await gatheringService.update(gatheringId, payload)
      : (clubId ? await gatheringService.create(clubId, payload) : await gatheringService.createStandalone(payload));
    setBusy(false);
    if (!res.success) { await showAlert({ title: 'COULD NOT SAVE', message: res.error?.message || 'Something went wrong.', type: 'error' }); return false; }
    savedDetails.current = JSON.stringify(details);
    clearDraft(draftId);
    setRecovered(null);
    const gid = gatheringId || res.data.id;
    if (!gatheringId) setGatheringId(gid);
    if (res.data) setLoaded((prev) => ({ ...(prev || {}), ...res.data }));
    if (!gatheringId) { setPendingCoHosts([]); await reloadGathering(gid); }
    await refreshAgenda(gid);
    setStep(2);
    return true;
  };

  // ── Overlap confirmation for a just-added/linked event ─────────────────────
  const maybeConfirmOverlap = async (eventId, list) => {
    const target = list.find((e) => e.id === eventId);
    if (!target) return;
    const conflicts = list.filter((e) => e.id !== eventId && overlaps(target, e));
    if (conflicts.length === 0) return;
    const c = conflicts[0];
    const more = conflicts.length > 1 ? ` (and ${conflicts.length - 1} more)` : '';
    const ok = await showConfirm({
      title: 'HEADS UP — TIMES OVERLAP',
      message: `"${target.title}" overlaps "${c.title}" (${timeRangeLabel(c.dateTime, c.endDateTime)})${more}. Gatherings can run parallel tracks — keep this on the agenda?`,
      confirmLabel: 'KEEP IT',
      cancelLabel: 'REMOVE IT',
    });
    if (!ok) {
      await gatheringService.detachEvent(gatheringId, eventId);
      await refreshAgenda();
    }
  };

  // ── Step 2: add a NEW event via the real builder (embedded) ────────────────
  const handleAdded = async (eventId) => {
    setAddOpen(false);
    setEditingEventId(null);
    const list = await refreshAgenda();
    await maybeConfirmOverlap(eventId, list);
  };

  // ── Step 2: link an EXISTING club event ────────────────────────────────────
  const openLinkPicker = async () => {
    setAttachable([]);
    setLinkOpen(true);
    const r = await gatheringService.listAttachableEvents(gatheringId);
    setAttachable(r.success ? (r.data || []).filter((e) => !e.inThisGathering) : []);
  };
  const linkEvent = async (ev) => {
    const r = await gatheringService.attachEvent(gatheringId, ev.id);
    if (!r.success) { await showAlert({ title: 'COULD NOT LINK', message: r.error?.message || 'Something went wrong.', type: 'error' }); return; }
    setLinkOpen(false);
    const list = await refreshAgenda();
    await maybeConfirmOverlap(ev.id, list);
  };
  const removeEvent = async (ev) => {
    const ok = await showConfirm({ title: 'REMOVE FROM THE AGENDA?', message: ev.title, confirmLabel: 'REMOVE', cancelLabel: 'KEEP', variant: 'danger' });
    if (!ok) return;
    await gatheringService.detachEvent(gatheringId, ev.id);
    refreshAgenda();
  };

  // ── Step 3: finish ─────────────────────────────────────────────────────────
  const finish = async (publish) => {
    setBusy(true);
    if (publish) {
      const r = await gatheringService.update(gatheringId, { status: 'published' });
      if (!r.success) { setBusy(false); await showAlert({ title: 'COULD NOT PUBLISH', message: r.error?.message || 'Something went wrong.', type: 'error' }); return; }
    }
    navigate(clubId ? `/clubs/${clubId}/gatherings/${gatheringId}` : `/gatherings/${gatheringId}`);
  };

  const goStep = async (n) => {
    if (n > 1 && (!gatheringId || hasUnsavedDetails)) {
      if (!await submitDetails()) return;
    }
    setStep(n);
  };

  // ── Derived ──
  const overlapMap = computeOverlaps(agenda);
  const overlapCount = Object.keys(overlapMap).length;
  const days = rangeDays(details.startDate, details.endDate);
  const sorted = [...agenda].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
  const lanes = days.map((d) => ({ key: d.key, label: d.long, events: sorted.filter((e) => dayKey(e.dateTime) === d.key), outside: false }));
  const dayKeys = new Set(days.map((d) => d.key));
  const outside = sorted.filter((e) => !dayKeys.has(dayKey(e.dateTime)));
  if (outside.length) lanes.push({ key: 'outside', label: 'OUTSIDE THE DATES', events: outside, outside: true });
  const ticketSum = agenda.filter((e) => e.ticketPriceCents > 0 && !e.isExternalTicketing).reduce((s, e) => s + e.ticketPriceCents, 0);
  const datesLine = `${shortDate(details.startDate)} – ${shortDate(details.endDate)}`;
  const starter = details.chargeFee && details.fundingType === 'party_starter';
  const panelPad = mobile ? '18px 16px' : '22px 24px';
  const gap = mobile ? 10 : 16;
  const backTo = async () => {
    if (hasUnsavedDetails && !await showConfirm({ title: 'LEAVE WITH UNSAVED CHANGES?',
      message: 'Your latest gathering details have not been saved.', confirmLabel: 'LEAVE', cancelLabel: 'KEEP EDITING' })) return;
    if (window.history.state?.idx > 0 || location.key !== 'default') navigate(-1);
    else navigate(editId ? (clubId ? `/clubs/${clubId}/gatherings/${editId}` : `/gatherings/${editId}`)
      : clubId ? `/clubs/${clubId}` : '/host');
  };

  const panel = (title, children, testId, sub) => (
    <section className="flex flex-col bg-white font-mono" style={{ gap: 18, padding: panelPad }} data-testid={testId}>
      <span className="flex flex-col" style={{ gap: 4 }}>
        <span style={kicker(12)}>{title}</span>
        {sub && <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{sub}</span>}
      </span>
      {children}
    </section>
  );

  // ── Stepper ──
  const stepper = (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 6, padding: mobile ? '0 12px' : 0 }} data-testid="stepper">
      {STEPS.map(([n, label]) => {
        const on = step === n;
        const done = step > n;
        return (
          <button key={n} type="button" onClick={() => goStep(n)} aria-current={on ? 'step' : undefined} className="flex items-center font-mono" data-testid={`step-${n}`}
            style={{ gap: mobile ? 6 : 10, height: mobile ? 44 : 50, padding: mobile ? '0 10px' : '0 16px', border: '2px solid transparent', borderRadius: 999, background: on ? RING_ON : '#fff', color: on ? '#fff' : done ? '#000' : SUBTLE, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', boxSizing: 'border-box', transition: 'background-color .2s, color .2s' }}>
            <span className="flex flex-shrink-0 items-center justify-center rounded-full" style={{ width: mobile ? 18 : 22, height: mobile ? 18 : 22, background: on ? '#fff' : done ? '#000' : '#F2F2F2', color: on ? '#000' : done ? '#fff' : SUBTLE, fontSize: 10, fontWeight: 800 }}>{done ? '✓' : n}</span>
            <span className="truncate" style={{ fontSize: 10, fontWeight: 700, letterSpacing: mobile ? '.08em' : '.14em' }}>{label}</span>
          </button>
        );
      })}
    </div>
  );

  // ── Step 1 ──
  const step1 = (
    <>
      {panel('THE BASICS', (
        <>
          <Field label="NAME"><input value={details.name} onChange={(e) => setD({ name: e.target.value })} placeholder="Fall release weekend" style={pillInput} data-testid="name-input" /></Field>
          <Field label="WHAT IT'S ABOUT · OPTIONAL"><textarea value={details.description} onChange={(e) => setD({ description: e.target.value })} placeholder="What's the weekend about?" style={softTextarea} /></Field>
          <div className="grid" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))', gap: 12 }}>
            <Field label="STARTS"><input type="date" value={details.startDate} onChange={(e) => setD({ startDate: e.target.value })} style={{ ...pillInput, fontSize: 13 }} data-testid="start-input" /></Field>
            <Field label="ENDS"><input type="date" value={details.endDate} onChange={(e) => setD({ endDate: e.target.value })} style={{ ...pillInput, fontSize: 13 }} data-testid="end-input" /></Field>
          </div>
          {dateErr && <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }} data-testid="date-error">The end date has to be on or after the start date.</span>}
          {touched && !dateErr && !detailsValid && <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }} data-testid="details-error">A name, a start date and an end date first.</span>}
        </>
      ), 'basics-panel')}
      {panel('WHO GETS IN', (
        <>
          <Segmented label="WHO CAN JOIN" value={details.mode} onChange={(k) => setD({ mode: k })} testId="mode-seg"
            options={[{ key: 'open', label: 'OPEN', hint: clubId ? 'Any member gets in instantly.' : 'Anyone gets in instantly.' }, { key: 'curated', label: 'CURATED', hint: 'You approve each person.' }]} />
          <Segmented label="VISIBILITY" value={details.isPrivate ? 'private' : 'listed'} onChange={(k) => setD({ isPrivate: k === 'private' })} testId="visibility-seg"
            options={[{ key: 'private', label: 'PRIVATE', hint: 'Hidden from listings. Link or invite only.' }, { key: 'listed', label: 'LISTED', hint: clubId ? 'Shows on the club page and the events wall.' : 'Shows on the events wall.' }]} />
        </>
      ), 'access-panel')}
      {canPickCoHosts && panel('CO-HOSTS', (
        <span className="flex flex-wrap items-center" style={{ gap: 6 }} data-testid="cohost-list">
          {coHostList.map((p) => (
            <FacePill key={p.userId} src={p.avatarUrl} name={p.displayName || 'Someone'} testId="cohost-chip"
              right={<button type="button" onClick={() => removeWizardCoHost(p)} aria-label={`Remove ${p.displayName || 'co-host'}`}
                style={{ width: 22, height: 22, border: 'none', borderRadius: 999, background: '#fff', fontSize: 12, lineHeight: 1, cursor: 'pointer' }}>×</button>} />
          ))}
          <PillButton variant="ghost" size={36} fontSize={10} onClick={() => setCoHostPickerOpen(true)} data-testid="add-cohost">+ ADD CO-HOST</PillButton>
        </span>
      ), 'cohost-panel', 'They run the whole gathering with you: agenda, invites, entry, the door and the money. Only you can delete it or change who co-hosts.')}
      <section className="flex flex-col bg-white font-mono" style={{ gap: 16, padding: panelPad }} data-testid="fee-panel">
        <span className="flex items-center justify-between" style={{ gap: 12 }}>
          <span className="flex flex-col" style={{ gap: 4 }}>
            <span style={kicker(12)}>ENTRY FEE</span>
            <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>A one-time charge to attend. Separate from each event's ticket.</span>
          </span>
          <BigSwitch on={details.chargeFee} onChange={(v) => setD({ chargeFee: v })} label="Charge an entry fee" testId="fee-switch" />
        </span>
        {details.chargeFee && (
          <div className="flex flex-col" style={{ gap: 16, paddingTop: 14, borderTop: RULE }}>
            <Field label="ENTRY FEE ($)" max={220}><input value={details.entryFee} onChange={(e) => setD({ entryFee: e.target.value })} inputMode="decimal" placeholder="0.00" style={{ ...pillInput, fontWeight: 700 }} data-testid="fee-input" /></Field>
            <Segmented label="FUNDING" value={details.fundingType} onChange={(k) => setD({ fundingType: k })} testId="funding-seg"
              options={[{ key: 'guaranteed', label: 'GUARANTEED', hint: 'Happens no matter what.' }, { key: 'party_starter', label: 'PARTY STARTER', hint: 'Only happens if it hits the goal.' }]} />
            {starter && (
              <>
                <div className="grid" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))', gap: 12 }}>
                  <Field label="FUNDING GOAL ($)"><input value={details.fundingGoal} onChange={(e) => setD({ fundingGoal: e.target.value })} inputMode="decimal" placeholder="0.00" style={{ ...pillInput, fontWeight: 700 }} /></Field>
                  <Field label="DEADLINE"><input type="date" value={details.fundingDeadline} onChange={(e) => setD({ fundingDeadline: e.target.value })} style={{ ...pillInput, fontSize: 13 }} /></Field>
                </div>
                <span style={{ padding: '12px 14px', borderRadius: 18, background: STARTER_TRACK, fontSize: 11, lineHeight: 1.55 }}>If it doesn't reach the goal by the deadline, it doesn't happen and every entry is refunded.</span>
              </>
            )}
          </div>
        )}
      </section>
    </>
  );

  // ── Step 2 ──
  const laneCard = (e) => {
    const clash = overlapMap[e.id];
    return (
      <div key={e.id} className="flex flex-col bg-white" style={{ gap: 6, padding: '14px 16px', boxShadow: clash ? `inset 4px 0 0 ${CLASH_BAR}` : 'none' }} data-testid={`lane-event-${e.id}`}>
        <span className="flex justify-between" style={{ gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: MUTED }}>{timeRangeLabel(e.dateTime, e.endDateTime)}</span>
          <span style={{ fontSize: 11, fontWeight: 800 }}>{priceLabel(e)}</span>
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{e.title}</span>
        {(e.venueName || e.locationCity) && <span style={{ fontSize: 10, color: MUTED }}>{[e.venueName, e.locationCity].filter(Boolean).join(' · ')}</span>}
        {clash && <OverlapChip title={clash[0].title} verb="OVERLAPS" size={8} />}
        <span className="flex" style={{ gap: 12, marginTop: 2 }}>
          <button type="button" onClick={() => { setEditingEventId(e.id); setAddOpen(true); }} className="hover:underline" style={{ ...kicker(9, '.14em'), border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }}>EDIT</button>
          <button type="button" onClick={() => removeEvent(e)} className="hover:underline" style={{ ...kicker(9, '.14em'), color: MUTED, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }}>REMOVE</button>
        </span>
      </div>
    );
  };
  const step2 = (
    <section className="flex flex-col" style={{ gap: 10 }} data-testid="agenda-step">
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 10, padding: mobile ? '16px 16px 0' : '4px 0 0' }}>
        <span className="flex flex-col" style={{ gap: 3 }}>
          <span style={kicker(12)}>THE AGENDA</span>
          <span style={{ ...kicker(10, '.14em'), color: SUBTLE }}>{plural(agenda.length, 'EVENT')} · {datesLine}</span>
        </span>
        <span className="flex" style={{ gap: 6 }}>
          <PillButton variant="ghost" size={40} fontSize={10} onClick={openLinkPicker} data-testid="link-existing">LINK EXISTING</PillButton>
          <PillButton size={40} fontSize={10} onClick={() => setAddOpen(true)} data-testid="add-event">+ ADD EVENT</PillButton>
        </span>
      </span>
      {overlapCount > 0 && (
        <div className="flex flex-wrap items-center" style={{ gap: '8px 12px', padding: '12px 16px', borderRadius: 18, background: CLASH_BG, margin: mobile ? '0 12px' : 0 }} data-testid="clash-banner">
          <TagPill bg={CLASH_FG} fg="#fff" size={9}>{overlapCount} OVERLAP</TagPill>
          <span style={{ fontSize: 12, lineHeight: 1.5, color: BODY }}>Times overlap. That's fine for parallel tracks. Make sure it's on purpose.</span>
        </div>
      )}
      <div className="grid items-start" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : `repeat(${Math.min(Math.max(lanes.length, 1), 4)},minmax(0,1fr))`, gap: mobile ? 10 : 8, padding: mobile ? '0 12px' : 0 }} data-testid="lanes">
        {lanes.length === 0 && (
          <div className="flex flex-col bg-white font-mono" style={{ gap: 6, padding: '28px 22px' }}>
            <span style={kicker(12)}>SET THE DATES FIRST.</span>
            <span style={{ fontSize: 12, color: MUTED }}>The agenda lays out one lane per day of the gathering.</span>
          </div>
        )}
        {lanes.map((l) => (
          <div key={l.key} className="flex min-w-0 flex-col" style={{ gap: 2 }} data-testid={`lane-${l.key}`}>
            <span className="flex items-center justify-between" style={{ gap: 8, padding: '14px 16px', background: l.outside ? RED_TINT : '#000', color: l.outside ? '#DC2626' : '#fff' }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.08em' }}>{l.label}</span>
              <span style={{ ...kicker(9, '.14em'), opacity: 0.7 }}>{plural(l.events.length, 'EVENT')}</span>
            </span>
            {l.events.map(laneCard)}
            {l.events.length === 0 && (
              <button type="button" onClick={() => setAddOpen(true)} className="font-mono" style={{ padding: '22px 16px', border: '2px dashed #D1D5DB', background: 'transparent', fontFamily: 'inherit', ...kicker(9, '.14em'), color: SUBTLE, cursor: 'pointer' }}>NOTHING YET · + ADD</button>
            )}
          </div>
        ))}
      </div>
    </section>
  );

  // ── Step 3 ──
  const review = [
    ['NAME', details.name || '—'],
    ['DATES', datesLine],
    ['AGENDA', `${plural(agenda.length, 'event').toLowerCase()} across ${plural(groupByDay(agenda).length, 'day').toLowerCase()}`],
    ['ALL TICKETS', money(ticketSum)],
    ['WHO CAN JOIN', details.mode === 'curated' ? 'You approve each person' : clubId ? 'Open to members' : 'Open'],
    ['ENTRY', details.chargeFee ? `${money(Math.round(parseFloat(details.entryFee || '0') * 100))} · ${starter ? `party starter, goal ${money(Math.round(parseFloat(details.fundingGoal || '0') * 100))}` : 'guaranteed'}` : 'None'],
    ['VISIBILITY', details.isPrivate ? 'Private · link or invite only' : 'Listed'],
  ];
  // The cover editor + the real cover at desktop width (CSS-scaled to fit) and in a phone frame.
  const previewScale = previewWidth > 0 ? Math.min(1, previewWidth / 1216) : 1;
  const coverIdentity = (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <span className="flex flex-wrap" style={{ gap: 8 }}>
        <TagPill bg="#00FFFF" size={10} padding="5px 12px">GATHERING</TagPill>
        <TagPill size={10} padding="5px 12px">{loaded?.status === 'published' ? 'SELLING' : 'DRAFT'}</TagPill>
      </span>
      <span style={{ fontSize: 40, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-.01em', overflowWrap: 'anywhere' }}>{details.name || 'Untitled gathering'}</span>
      {details.description && <span style={{ maxWidth: 520, fontSize: 14, lineHeight: 1.65, color: BODY }}>{details.description}</span>}
      <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '.04em' }}>{datesLine}</span>
    </div>
  );
  const coverPanel = gatheringId && loaded && (
    <section className="flex flex-col font-mono" style={{ gap: 10 }} data-testid="cover-step">
      <ProCoverEditor pageId={gatheringId} api={coverApi} copy={GATHERING_COVER_COPY} canRemove showVideo={false} testId="gathering-cover-editor"
        cover={shapedCover} setCover={setCover} onReload={() => reloadGathering()} onClose={() => setStep(2)} phone={mobile} />
      <div ref={previewRef} className="flex flex-col" style={{ gap: 8, padding: mobile ? '0 12px' : 0 }} data-testid="cover-preview">
        <span style={{ ...kicker(9), color: MUTED, padding: '0 4px' }}>LIVE PREVIEW · {mobile ? 'PHONE' : 'DESKTOP'}</span>
        {mobile ? (
          <GatheringCover gathering={loaded} photos={coverPhotos} layout={layoutKey} split={shapedCover.split} phone host editing onFocal={onFocal} width={390} />
        ) : (
          <div style={{ width: '100%', height: Math.round((previewWidth >= 1100 ? 480 : 440) * previewScale), overflow: 'hidden' }}>
            <div style={{ width: 1216, transform: `scale(${previewScale})`, transformOrigin: 'top left' }}>
              <GatheringCover gathering={loaded} identity={coverIdentity} photos={coverPhotos} layout={layoutKey} split={shapedCover.split} host editing onFocal={onFocal} width={1216} />
            </div>
          </div>
        )}
      </div>
    </section>
  );

  const step3 = (
    <>
      {coverPanel}
      <section className="flex flex-col bg-white font-mono" style={{ gap: 14, padding: panelPad }} data-testid="review-panel">
        <span className="flex justify-between" style={{ gap: 8 }}>
          <span style={kicker(12)}>READ IT BACK</span>
          <span style={{ ...kicker(9, '.14em'), color: SUBTLE }}>{loaded?.status === 'published' ? 'LIVE' : 'NOTHING IS LIVE YET'}</span>
        </span>
        <KVRows rows={review} testId="review-rows" />
        {overlapCount > 0 && <span style={{ padding: '12px 14px', borderRadius: 18, background: CLASH_BG, fontSize: 11, lineHeight: 1.55 }}>{overlapCount} events share time slots. Guests see them flagged as parallel tracks.</span>}
      </section>
      <section className="flex flex-col bg-white font-mono" style={{ gap: 10, padding: panelPad }}>
        <span style={kicker(12)}>AGENDA</span>
        {lanes.map((l) => (
          <div key={l.key} className="flex flex-col" style={{ gap: 6, paddingTop: 10, borderTop: RULE }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: l.outside ? '#DC2626' : '#000' }}>{l.label}</span>
            {l.events.map((e) => (
              <span key={e.id} className="flex flex-wrap" style={{ gap: '4px 14px', fontSize: 12 }}>
                <span style={{ flex: '0 0 140px', color: MUTED }}>{timeRangeLabel(e.dateTime, e.endDateTime)}</span>
                <span style={{ flex: '1 1 180px', fontWeight: 700 }}>{e.title}</span>
                <span style={{ fontWeight: 800 }}>{priceLabel(e)}</span>
              </span>
            ))}
            {l.events.length === 0 && <span style={{ fontSize: 11, color: SUBTLE }}>Nothing on this day.</span>}
          </div>
        ))}
      </section>
    </>
  );

  // ── Footer ──
  const next = () => { if (step === 1) submitDetails(); else if (step === 2) setStep(3); else finish(true); };
  const nextLabel = step === 1 ? 'CONTINUE TO AGENDA →' : step === 2 ? 'COVER & REVIEW →' : loaded?.status === 'published' ? 'DONE' : 'PUBLISH';
  const footer = (
    <div className="flex justify-between" style={{ gap: 8, position: mobile ? 'sticky' : 'static', bottom: mobile ? 'calc(64px + env(safe-area-inset-bottom, 0px))' : undefined, zIndex: 20, padding: mobile ? '10px 12px' : 0, background: mobile ? '#F5F5F5' : 'transparent' }} data-testid="wizard-footer">
      {step > 1 && <PillButton variant="ghost" size={50} onClick={() => setStep(step - 1)}>← BACK</PillButton>}
      <span style={{ flex: 1 }} />
      {step === 3 && <PillButton variant="ghost" size={50} disabled={busy} onClick={() => finish(false)} data-testid="save-draft">{mobile ? 'DRAFT' : loaded?.status === 'published' ? 'KEEP IT LIVE' : 'SAVE AS DRAFT'}</PillButton>}
      <PillButton variant={step === 3 ? 'ring' : 'primary'} size={50} disabled={busy} onClick={next} data-testid="wizard-next">{busy ? 'SAVING…' : nextLabel}</PillButton>
    </div>
  );

  // ── Preview rail ──
  const chips = [details.mode === 'curated' ? 'CURATED' : 'OPEN', details.isPrivate ? 'PRIVATE' : 'LISTED', ...(details.chargeFee ? [`ENTRY ${money(Math.round(parseFloat(details.entryFee || '0') * 100))}`] : []), ...(starter ? ['PARTY STARTER'] : [])];
  const preview = (
    <div className="flex flex-col font-mono" style={{ gap: 10 }} data-testid="preview-card">
      <span style={{ ...kicker(9), color: MUTED, padding: '0 4px' }}>{clubId ? 'HOW IT READS IN THE CLUB' : 'HOW IT READS'}</span>
      <div className="flex flex-col bg-white">
        <div style={{ height: 150, background: loaded?.posterUrl ? '#111' : posterPlaceholder(gatheringId || details.name || 'new') }}>
          {loaded?.posterUrl && <img src={loaded.posterUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </div>
        <div className="flex flex-col" style={{ padding: '18px 20px', gap: 10 }}>
          <span className="flex" style={{ gap: 6 }}>
            <TagPill bg="#00FFFF" size={9} padding="4px 10px">GATHERING</TagPill>
            <TagPill size={9} padding="4px 10px">{loaded?.status === 'published' ? 'SELLING' : 'DRAFT'}</TagPill>
          </span>
          <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15, overflowWrap: 'anywhere' }}>{details.name || 'Untitled gathering'}</span>
          {details.description && <span style={{ fontSize: 11, lineHeight: 1.55, color: BODY }}>{details.description}</span>}
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em' }}>{datesLine} · {plural(agenda.length, 'EVENT')}</span>
          <span className="flex flex-wrap" style={{ gap: 6 }}>{chips.map((c) => <TagPill key={c} bg="#F5F5F5" size={9} padding="5px 10px">{c}</TagPill>)}</span>
          <span className="flex items-baseline justify-between" style={{ paddingTop: 10, borderTop: RULE }}>
            <span style={{ ...kicker(9, '.14em'), color: MUTED }}>ALL TICKETS</span>
            <span style={{ fontSize: 18, fontWeight: 800 }}>{money(ticketSum)}</span>
          </span>
        </div>
      </div>
    </div>
  );

  const body = (
    <main ref={mainRef} className="flex w-full flex-col font-mono" style={{ maxWidth: 1180, margin: '0 auto', padding: mobile ? '0 0 16px' : '24px 32px 72px', gap, boxSizing: 'border-box' }} data-testid="gathering-wizard">
      <span className="flex flex-col" style={{ gap: 10, padding: mobile ? '16px 16px 0' : '4px 0 0' }}>
        <button type="button" onClick={backTo} className="text-left" style={{ ...kicker(10), border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }}>← BACK</button>
        <h1 style={{ margin: 0, textTransform: 'none', fontSize: mobile ? 26 : 36, fontWeight: 700, lineHeight: 1.1 }}>{isEdit ? 'Edit gathering' : 'New gathering'}</h1>
        <span style={{ fontSize: 12, lineHeight: 1.6, color: BODY, maxWidth: 560 }}>{isEdit ? 'Add or remove events, change the details, publish when it reads right.' : 'A few days of your events under one name. Guests can take all of it or pick nights. We flag time clashes as you go.'}</span>
      </span>
      {recovered && (
        <div className="flex flex-wrap items-center bg-white font-mono" style={{ gap: '10px 14px', padding: mobile ? '14px 16px' : '16px 20px', margin: mobile ? '0 12px' : 0 }} data-testid="draft-banner">
          <span style={{ flex: '1 1 260px', fontSize: 12, lineHeight: 1.55 }}>
            <span style={{ fontWeight: 700 }}>You were part-way through this.</span> Unsaved changes from {new Date(recovered.at).toLocaleString()} are still here.
          </span>
          <span className="flex" style={{ gap: 6 }}>
            <PillButton size={36} fontSize={10} onClick={() => { setDetails((d) => ({ ...d, ...recovered.details })); setRecovered(null); setStep(1); }} data-testid="draft-restore">PICK IT BACK UP</PillButton>
            <PillButton variant="ghost" size={36} fontSize={10} onClick={() => { clearDraft(draftId); setRecovered(null); }} data-testid="draft-discard">START FRESH</PillButton>
          </span>
        </div>
      )}
      {stepper}
      <div className="grid items-start" style={{ gridTemplateColumns: wide ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)', gap }}>
        <div className="flex min-w-0 flex-col" style={{ gap }}>
          {step === 1 && step1}
          {step === 2 && step2}
          {step === 3 && step3}
          {footer}
        </div>
        {wide && <StickyRail width={380}>{preview}</StickyRail>}
      </div>
    </main>
  );

  const sheets = (
    <>
      {/* Add-event modal — the real Event builder, embedded */}
      <AddPeopleModal title="Add a co-host" ctaLabel="Make co-host" placeholder="Search people to co-host…" isOpen={coHostPickerOpen}
        onClose={() => setCoHostPickerOpen(false)} onAdd={addWizardCoHosts}
        excludeUserIds={[profile?.userId, ...(loaded?.hosts || []).map((h) => h.userId), ...coHostList.map((c) => c.userId)].filter(Boolean)} />
      <Modal isOpen={addOpen} onClose={() => { setAddOpen(false); setEditingEventId(null); }} title={editingEventId ? "Edit event" : "Add an event"} size="full" closeOnOverlayClick={false}>
        {gatheringId && (
          <EventCreatePage key={editingEventId || 'new'} embedded eventId={editingEventId} clubIdProp={clubId ?? null} gatheringId={gatheringId} onComplete={handleAdded} onCancel={() => { setAddOpen(false); setEditingEventId(null); }} />
        )}
      </Modal>
      <Sheet open={linkOpen} onClose={() => setLinkOpen(false)} phone={mobile} title="LINK AN EXISTING EVENT" testId="link-sheet">
        <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>{clubId ? "This club's events" : 'Your events'} that aren't on an agenda yet. Linking one keeps its own page, tickets and guest list.</span>
        {attachable.length === 0 ? (
          <span style={{ fontSize: 12, color: MUTED }}>No unattached {clubId ? 'club ' : ''}events. Add a new one instead.</span>
        ) : (
          <div className="flex flex-col" style={{ gap: 4 }}>
            {attachable.map((ev) => {
              const other = !!ev.gatheringId && !ev.inThisGathering;
              const inRange = !days.length || dayKeys.has(dayKey(ev.dateTime));
              return (
                <button key={ev.id} type="button" disabled={other} onClick={() => linkEvent(ev)} className="flex items-center text-left font-mono" data-testid={`link-${ev.id}`}
                  style={{ gap: 12, padding: '12px 14px', border: 'none', borderRadius: 16, background: '#F5F5F5', opacity: other ? 0.45 : 1, fontFamily: 'inherit', cursor: other ? 'default' : 'pointer' }}>
                  <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{ev.title}</span>
                    <span style={{ fontSize: 10, color: MUTED }}>{whenLabel(ev.dateTime)}{!inRange && !other ? ' · OUTSIDE THESE DATES' : ''}</span>
                  </span>
                  <span style={{ ...kicker(9, '.14em'), flexShrink: 0 }}>{other ? 'IN ANOTHER GATHERING' : 'LINK →'}</span>
                </button>
              );
            })}
          </div>
        )}
      </Sheet>
    </>
  );

  return (
    <SoftSurface>
      {mobile ? (
        <SurfacePage mobileTitle={isEdit ? 'EDIT GATHERING' : 'NEW GATHERING'} mobileEyebrow={`STEP ${step} OF 3`} onBack={backTo}>{body}{sheets}</SurfacePage>
      ) : (
        <SurfacePage bare>{body}{sheets}</SurfacePage>
      )}
    </SoftSurface>
  );
}
